'use strict'

import $ from 'jquery'

const RULES = [
    { key: 'length', label: 'At least 9 characters', test: (v) => v.length >= 9 },
    { key: 'upper', label: 'At least one uppercase letter (A–Z)', test: (v) => /[A-Z]/.test(v) },
    { key: 'lower', label: 'At least one lowercase letter (a–z)', test: (v) => /[a-z]/.test(v) },
    { key: 'number', label: 'At least one number (0–9)', test: (v) => /[0-9]/.test(v) },
    { key: 'special', label: 'At least one special character (!@#$%&)', test: (v) => /[!@#$%&]/.test(v) },
]

const TOAST_DURATION = 2500

// ----- Strength checklist (only on the first password field) -----

const buildChecklist = () => {
    const items = RULES.map(({ key, label }) => `<li class="password-checklist__item" data-rule="${key}">${label}</li>`).join('')

    return `<div class="password-checklist">
        <ul class="password-checklist__list">${items}</ul>
        <div class="password-strength">
            <div class="password-strength__bar"></div>
        </div>
    </div>`
}

const updateChecklist = ($input, $checklist) => {
    const value = $input.val()
    const metCount = RULES.reduce((count, { key, test }) => {
        const met = test(value)
        $checklist.find(`[data-rule="${key}"]`).toggleClass('password-checklist__item--met', met)
        return count + (met ? 1 : 0)
    }, 0)

    const $bar = $checklist.find('.password-strength__bar')
    $bar.removeClass('password-strength__bar--weak password-strength__bar--medium password-strength__bar--strong')

    if (value.length === 0) return

    if (metCount <= 2) {
        $bar.addClass('password-strength__bar--weak')
    } else if (metCount <= 4) {
        $bar.addClass('password-strength__bar--medium')
    } else {
        $bar.addClass('password-strength__bar--strong')
    }
}

// ----- Toast (transient warning bubble above the input) -----

// Returns a `flash(message)` function that shows the message for TOAST_DURATION, then fades it out.
const setupToast = ($inputGroup) => {
    const $toast = $('<span class="password-toast-warning"></span>').hide()
    $inputGroup.css('position', 'relative').append($toast)

    let timer = null
    return (message) => {
        if (!message) return
        $toast.text(message).stop(true, true).fadeIn(150)
        clearTimeout(timer)
        timer = setTimeout(() => $toast.fadeOut(300), TOAST_DURATION)
    }
}

const readMessages = ($input) => ({
    noSpaces: $input.attr('data-no-spaces-message'),
    pasteNoSpaces: $input.attr('data-paste-no-spaces-message'),
    pasteTrimmed: $input.attr('data-paste-trimmed-message'),
    maxLength: $input.attr('data-max-length-message'),
})

// ----- Tiny helpers used by paste handler -----

// Length of the input AFTER inserting `text` at the current selection.
const projectedLength = ($input, text) => {
    const { selectionStart, selectionEnd } = $input[0]
    return $input.val().length - (selectionEnd - selectionStart) + text.length
}

// Replace the current selection with `text`, optionally trimming to `maxLength`.
// Returns true if the result had to be trimmed.
const replaceSelection = ($input, text, maxLength) => {
    const { selectionStart: start, selectionEnd: end } = $input[0]
    const currentVal = $input.val()

    let newVal = currentVal.slice(0, start) + text + currentVal.slice(end)
    const trimmed = !isNaN(maxLength) && newVal.length > maxLength
    if (trimmed) newVal = newVal.slice(0, maxLength)

    $input.val(newVal)
    const newCursor = Math.min(start + text.length, newVal.length)
    $input[0].setSelectionRange(newCursor, newCursor)
    $input.trigger('input')

    return trimmed
}

// ----- Event handlers (UKS-1041) -----

// Block the space key from being typed.
const setupSpaceBlocking = ($input, messages, flash) => {
    if (!messages.noSpaces) return
    $input.on('keydown', (e) => {
        if (e.key === ' ') {
            e.preventDefault()
            flash(messages.noSpaces)
        }
    })
}

// Show a toast when typing while already at maxlength (browser silently blocks the input).
const setupMaxLengthKeydown = ($input, message, flash) => {
    const maxLength = parseInt($input.attr('maxlength'), 10)
    if (isNaN(maxLength)) return
    $input.on('keydown', (e) => {
        const isPrintable = e.key?.length === 1 && !e.ctrlKey && !e.metaKey
        if (isPrintable && e.key !== ' ' && $input.val().length >= maxLength) {
            flash(message)
        }
    })
}

const setupPasteHandler = ($input, messages, flash) => {
    const maxLength = parseInt($input.attr('maxlength'), 10)

    $input.on('paste', (e) => {
        const pasted = (e.originalEvent.clipboardData || window.clipboardData).getData('text')
        const cleaned = pasted.replace(/\s/g, '')
        const hasSpaces = pasted !== cleaned
        const wouldExceed = !isNaN(maxLength) && projectedLength($input, cleaned) > maxLength

        // Case 1: clean and fits — nothing to do
        if (!hasSpaces && !wouldExceed) return

        // Case 2: clean but too long — browser truncates by itself, we just notify
        if (!hasSpaces && wouldExceed) {
            flash(messages.maxLength)
            return
        }

        // Case 3 & 4: spaces present — we must take over the paste
        e.preventDefault()
        const trimmed = replaceSelection($input, cleaned, maxLength)
        flash(trimmed ? messages.pasteTrimmed : messages.pasteNoSpaces)
    })
}

// Full init for the first password field: checklist + strength bar + space/paste/maxlength handling.
const initStrength = ($input) => {
    const $formGroup = $input.closest('.form-group')
    const $inputGroup = $input.closest('.input-group')
    const flash = setupToast($inputGroup)
    const messages = readMessages($input)

    const $checklist = $(buildChecklist()).hide()
    $inputGroup.after($checklist)

    setupSpaceBlocking($input, messages, flash)
    setupPasteHandler($input, messages, flash)
    setupMaxLengthKeydown($input, messages.maxLength, flash)

    const passwordSelector = '#' + $input.attr('id')

    $input.on('focus', () => $checklist.show())
    $input.on('keyup input', () => {
        updateChecklist($input, $checklist)
        const $confirm = $(`[data-confirm-password="${passwordSelector}"]`)
        if ($confirm.val()) {
            $confirm.data('prevState', null)
            $confirm.trigger('change')
        }
    })

    $formGroup.on('focusout', (e) => {
        if (!$formGroup[0].contains(e.relatedTarget)) {
            $checklist.hide()
        }
    })
}

export const initPasswordStrength = () => {
    $('input[data-password-strength="1"]').each((_, element) => {
        initStrength($(element))
    })
}
